import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Chip,
  Card,
  CardContent,
  ListItem,
  ListItemButton,
  ListItemText,
  RadioGroup,
  FormControlLabel,
  Radio,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useNavigate } from 'react-router-dom';
import { getPlans, savePlans, getSettings, getAttempts, calculateMasteryMetrics } from '../utils/storage';
import { getChapters, getVerses } from '../services/bibleApi';
import { bibleBooks } from '../utils/books';
import { generateRangeReference } from '../utils/rangeUtils';

const Plans = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [newPlanName, setNewPlanName] = useState('');

  // Verse picker state
  const [selectionMode, setSelectionMode] = useState('single');
  const [startRef, setStartRef] = useState(null);
  const [endRef, setEndRef] = useState(null);
  const [pickerBook, setPickerBook] = useState('GEN');
  const [pickerChapter, setPickerChapter] = useState(1);
  const [pickerVerse, setPickerVerse] = useState(1);
  const [selectedVersesForPlan, setSelectedVersesForPlan] = useState([]);
  const [availableChapters, setAvailableChapters] = useState([]);
  const [availableVerses, setAvailableVerses] = useState([]);

  const loadChaptersForBook = useCallback(async () => {
    try {
      const chapters = await getChapters(getSettings().selectedBibleId, pickerBook);
      setAvailableChapters(chapters);
      if (chapters.length > 0 && (!pickerChapter || pickerChapter > chapters.length)) {
        setPickerChapter(1);
      }
    } catch (error) {
      console.warn('Error loading chapters:', error);
      setAvailableChapters(Array.from({ length: pickerBook === 'PSA' ? 150 : pickerBook === 'MAT' ? 28 : 50 }, (_, i) => ({ id: (i + 1).toString(), number: i + 1 })));
    }
  }, [pickerBook, pickerChapter]);

  const loadVersesForChapter = useCallback(async () => {
    try {
      const verses = await getVerses(getSettings().selectedBibleId, `${pickerBook}.${pickerChapter}`);
      setAvailableVerses(verses);
      if (verses.length > 0 && (!pickerVerse || pickerVerse > verses.length)) {
        setPickerVerse(1);
      }
    } catch (error) {
      console.warn('Error loading verses:', error);
      setAvailableVerses(Array.from({ length: 30 }, (_, i) => ({ id: (i + 1).toString(), number: i + 1 })));
    }
  }, [pickerBook, pickerChapter, pickerVerse]);

  useEffect(() => {
    setPlans(getPlans());
  }, []);

  useEffect(() => {
    if (open) {
      // Reset verse picker state when opening dialog
      setStartRef(null);
      setEndRef(null);
      setSelectionMode('single');
      setPickerBook('GEN');
      setPickerChapter(1);
      setPickerVerse(1);
      setAvailableVerses([]);
      loadChaptersForBook();
    }
  }, [open, loadChaptersForBook]);

  useEffect(() => {
    if (pickerBook && getSettings().selectedBibleId) {
      loadChaptersForBook();
    }
  }, [pickerBook, loadChaptersForBook]);

  useEffect(() => {
    if (pickerBook && pickerChapter && getSettings().selectedBibleId) {
      loadVersesForChapter();
    }
  }, [pickerBook, pickerChapter, loadVersesForChapter]);

  const handleBookSelect = (bookId) => {
    setPickerBook(bookId);
    setPickerChapter(1);
    setPickerVerse(1);
  };

  const handleChapterSelect = (chapterNum) => {
    setPickerChapter(chapterNum);
    setPickerVerse(1);
  };

  const handleVerseSelect = (verseNum) => {
    if (selectionMode === 'single') {
      setStartRef(null);
      setEndRef(null);
      setPickerVerse(verseNum);
    } else if (selectionMode === 'range') {
      if (!startRef) {
        // Set start verse
        setStartRef({ book: pickerBook, chapter: pickerChapter, verse: verseNum });
        setEndRef(null);
        setPickerVerse(verseNum);
      } else if (!endRef) {
        // Set end verse
        const end = { book: pickerBook, chapter: pickerChapter, verse: verseNum };
        const start = startRef;
        // Ensure start is before end
        const startBookIndex = bibleBooks.findIndex(b => b.id === start.book);
        const endBookIndex = bibleBooks.findIndex(b => b.id === end.book);
        if (startBookIndex < endBookIndex || (startBookIndex === endBookIndex && (start.chapter < end.chapter || (start.chapter === end.chapter && start.verse <= end.verse)))) {
          setEndRef(end);
        } else {
          // Swap
          setEndRef(start);
          setStartRef(end);
        }
        setPickerVerse(verseNum);
      } else {
        // Reset if both set
        setStartRef({ book: pickerBook, chapter: pickerChapter, verse: verseNum });
        setEndRef(null);
        setPickerVerse(verseNum);
      }
    }
  };

  const handleAddVerse = () => {
    let reference = '';

    if (selectionMode === 'single') {
      const selectedBook = bibleBooks.find(b => b.id === pickerBook);
      const bookName = selectedBook ? selectedBook.name : pickerBook;
      reference = `${bookName} ${pickerChapter}:${pickerVerse}`;
    } else if (selectionMode === 'range' && startRef && endRef) {
      reference = generateRangeReference(startRef.book, startRef.chapter, startRef.verse, endRef.book, endRef.chapter, endRef.verse);
    }

    // Check if this reference is already selected
    if (reference && !selectedVersesForPlan.includes(reference)) {
      setSelectedVersesForPlan([...selectedVersesForPlan, reference]);
      // Reset picker state after adding
      setStartRef(null);
      setEndRef(null);
      setSelectionMode('single');
      // Keep current picker position for convenience
    }
  };

  const canAddCurrentSelection = () => {
    let reference = '';

    if (selectionMode === 'single') {
      const selectedBook = bibleBooks.find(b => b.id === pickerBook);
      const bookName = selectedBook ? selectedBook.name : pickerBook;
      reference = `${bookName} ${pickerChapter}:${pickerVerse}`;
    } else if (selectionMode === 'range' && startRef && endRef) {
      reference = generateRangeReference(startRef.book, startRef.chapter, startRef.verse, endRef.book, endRef.chapter, endRef.verse);
    }

    return reference && !selectedVersesForPlan.includes(reference);
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setNewPlanName('');
    setSelectedVersesForPlan([]);
    setStartRef(null);
    setEndRef(null);
    setSelectionMode('single');
  };

  const handleAddPlan = () => {
    if (!newPlanName.trim() || selectedVersesForPlan.length === 0) return;
    const newPlan = {
      id: Date.now().toString(),
      name: newPlanName,
      verses: selectedVersesForPlan,
      createdAt: Date.now(),
    };
    const updatedPlans = [...plans, newPlan];
    setPlans(updatedPlans);
    savePlans(updatedPlans);
    handleClose();
  };



  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
      {/* Header */}
      <Card
        sx={{
          p: 4,
          mb: 4,
          borderRadius: theme.shape.borderRadius * 2,
          backgroundColor: 'surfaceContainerHighest',
          border: `1px solid ${theme.palette.outline.variant}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <LibraryBooksIcon sx={{ mr: 2, fontSize: 40, color: theme.palette.primary.main }} />
          <Box>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                color: theme.palette.text.primary, 
                fontWeight: 600,
                mb: 1
              }}
            >
              Your Bible Memory Plans
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ color: theme.palette.text.secondary }}
            >
              Create and manage your personalized scripture memorization plans
            </Typography>
          </Box>
        </Box>
      </Card>

      {/* Add Plan Button */}
      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleClickOpen}
          size="large"
          sx={{ 
            px: 4,
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: theme.shape.borderRadius * 1.5,
            background: `linear-gradient(145deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            boxShadow: theme.shadows[2],
            '&:hover': {
              background: `linear-gradient(145deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
              boxShadow: theme.shadows[4],
              transform: 'translateY(-1px)'
            },
            transition: 'all 0.2s ease-in-out'
          }}
        >
          Add New Plan
        </Button>
      </Box>

      {/* Plans Cards */}
      {plans.length === 0 ? (
        <Box sx={{ p: 6, textAlign: 'center' }}>
          <LibraryBooksIcon sx={{ fontSize: 64, color: theme.palette.primary.main, mb: 2, opacity: 0.6 }} />
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 500,
              mb: 1
            }}
          >
            No plans yet
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary, opacity: 0.8 }}
          >
            Create your first memorization plan to get started
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {plans.map((plan) => {
            // Calculate plan progress
            const attempts = getAttempts();
            const planVerses = plan.verses || [];
            const verseAttempts = {};
            attempts.forEach(attempt => {
              if (planVerses.includes(attempt.verseId)) {
                if (!verseAttempts[attempt.verseId]) {
                  verseAttempts[attempt.verseId] = [];
                }
                verseAttempts[attempt.verseId].push(attempt);
              }
            });

            let masteredCount = 0;
            Object.keys(verseAttempts).forEach(verseId => {
              const atts = verseAttempts[verseId];
              const metrics = calculateMasteryMetrics(atts);
              if (metrics.isMastered) {
                masteredCount++;
              }
            });

            const progressPercentage = planVerses.length > 0 ? (masteredCount / planVerses.length) * 100 : 0;
            const progressColor = masteredCount === planVerses.length && planVerses.length > 0 ? 'success' : masteredCount > 0 ? 'primary' : 'default';

            return (
              <Grid item xs={12} sm={6} md={4} key={plan.id}>
                <Card
                  sx={{
                    borderRadius: theme.shape.borderRadius * 2,
                    backgroundColor: 'background.surfaceContainerLow',
                    border: `1px solid ${theme.palette.outline.light}`,
                    height: '100%',
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'visible',
                    '&:hover': {
                      backgroundColor: 'background.surfaceContainer',
                      transform: 'translateY(-2px)',
                      boxShadow: theme.shadows[4],
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                  onClick={() => navigate(`/plan/${plan.id}`)}
                >
                  <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography variant="body1" sx={{ color: theme.palette.text.primary, fontWeight: 500, flex: 1 }}>
                        {plan.name}
                      </Typography>
                      <Chip
                        size="small"
                        label={`${masteredCount}/${planVerses.length}`}
                        sx={{
                          backgroundColor: `${theme.palette[progressColor]?.main || theme.palette.primary.main}20`,
                          color: theme.palette[progressColor]?.main || theme.palette.primary.main,
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                      {planVerses.map((verse, index) => (
                        <Chip
                          key={index}
                          label={verse}
                          size="small"
                          sx={{
                            fontSize: '0.75rem',
                            height: 22,
                            backgroundColor: `${['primary', 'secondary', 'info', 'warning'][index % 4]}.${['main', 'light', 'dark'][index % 3]}15`,
                            color: ['primary', 'secondary', 'info', 'warning'][index % 4] + '.main',
                            border: `1px solid ${['primary', 'secondary', 'info', 'warning'][index % 4]}.${['main', 'light', 'dark'][index % 3]}30`,
                            '& .MuiChip-label': {
                              px: 1,
                            }
                          }}
                        />
                      ))}
                    </Box>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                      Progress: {Math.round(progressPercentage)}% mastered
                    </Typography>
                    <Box sx={{ mt: 'auto' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PlayArrowIcon />}
                        sx={{ borderRadius: theme.shape.borderRadius }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/plan/${plan.id}`);
                        }}
                      >
                        Continue Plan
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Add Plan Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: theme.shape.borderRadius * 2,
            background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.surfaceVariant} 100%)`,
            border: `1px solid ${theme.palette.outline.light}`,
            boxShadow: theme.shadows[6],
            height: '80vh',
            maxHeight: '700px'
          }
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: `1px solid ${theme.palette.outline.light}`,
            pb: 2,
            color: theme.palette.text.primary,
            fontWeight: 600
          }}
        >
          Create New Memorization Plan
        </DialogTitle>
        <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <TextField
            autoFocus
            margin="dense"
            label="Plan Name"
            fullWidth
            variant="outlined"
            value={newPlanName}
            onChange={(e) => setNewPlanName(e.target.value)}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: theme.shape.borderRadius,
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                  borderWidth: '2px',
                },
              },
              '& .MuiInputLabel-root': {
                color: theme.palette.text.secondary,
                '&.Mui-focused': {
                  color: theme.palette.primary.main,
                },
              },
            }}
            placeholder="e.g., John 3:16 Study Plan"
          />

          {/* Selected Verses Display */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              Selected Verses ({selectedVersesForPlan.length})
            </Typography>
            {selectedVersesForPlan.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedVersesForPlan.map((verse, index) => (
                  <Chip
                    key={index}
                    label={verse}
                    onDelete={() => {
                      const newVerses = selectedVersesForPlan.filter(v => v !== verse);
                      setSelectedVersesForPlan(newVerses);
                    }}
                    color="primary"
                    variant="outlined"
                    sx={{ fontWeight: 500 }}
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontStyle: 'italic' }}>
                No verses selected yet
              </Typography>
            )}
          </Box>

          {/* Selection Mode */}
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}>
              <RadioGroup
                row
                value={selectionMode}
                onChange={(e) => {
                  const newMode = e.target.value;
                  setSelectionMode(newMode);
                  setStartRef(null);
                  setEndRef(null);
                }}
                sx={{ justifyContent: 'center' }}
              >
                <FormControlLabel
                  value="single"
                  control={<Radio />}
                  label="Single Verse"
                />
                <FormControlLabel
                  value="range"
                  control={<Radio />}
                  label="Range"
                />
              </RadioGroup>
            </Box>
          </Box>


          {/* Range Mode Instructions */}
          {selectionMode === 'range' && (
            <Box sx={{ mb: 2, textAlign: 'center' }}>
              {startRef && endRef ? (
                <Box>
                  <Box sx={{ mb: 3, textAlign: 'center' }}>
                    <Chip
                      label={`Range selected: ${generateRangeReference(startRef.book, startRef.chapter, startRef.verse, endRef.book, endRef.chapter, endRef.verse)}`}
                      variant="outlined"
                      color="primary"
                      sx={{
                        fontWeight: 500,
                        width: 'fit-content',
                        mx: 'auto'
                      }}
                    />
                  </Box>
                  <Box sx={{ mb: 3, textAlign: 'center' }}>
                    <IconButton
                      onClick={handleAddVerse}
                      disabled={!canAddCurrentSelection()}
                      sx={{
                        color: canAddCurrentSelection()
                          ? theme.palette.primary.contrastText
                          : theme.palette.action.disabled,
                        backgroundColor: canAddCurrentSelection()
                          ? theme.palette.primary.main
                          : theme.palette.action.disabledBackground,
                        '&:hover': {
                          backgroundColor: canAddCurrentSelection()
                            ? theme.palette.primary.dark
                            : 'transparent',
                          color: canAddCurrentSelection()
                            ? theme.palette.primary.contrastText
                            : theme.palette.action.disabled,
                        },
                        '&.Mui-disabled': {
                          backgroundColor: theme.palette.action.disabledBackground,
                          color: theme.palette.action.disabled,
                        }
                      }}
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>
                </Box>
              ) : startRef ? (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Start: {bibleBooks.find(b => b.id === startRef.book)?.name || startRef.book} {startRef.chapter}:{startRef.verse} - Select end verse
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Select start verse
                </Typography>
              )}
            </Box>
          )}

          {/* Single Verse Selection Display */}
          {selectionMode === 'single' && (
            <Box sx={{ mb: 2, textAlign: 'center' }}>
              <Box sx={{ mb: 3, textAlign: 'center' }}>
                <Chip
                  label={`Verse selected: ${bibleBooks.find(b => b.id === pickerBook)?.name || pickerBook} ${pickerChapter}:${pickerVerse}`}
                  variant="outlined"
                  color="primary"
                  sx={{
                    fontWeight: 500
                  }}
                />
              </Box>
              <Box sx={{ mb: 3, textAlign: 'center' }}>
                <IconButton
                  onClick={handleAddVerse}
                  disabled={!canAddCurrentSelection()}
                  sx={{
                    color: canAddCurrentSelection()
                      ? theme.palette.primary.contrastText
                      : theme.palette.action.disabled,
                    backgroundColor: canAddCurrentSelection()
                      ? theme.palette.primary.main
                      : theme.palette.action.disabledBackground,
                    '&:hover': {
                      backgroundColor: canAddCurrentSelection()
                        ? theme.palette.primary.dark
                        : 'transparent',
                      color: canAddCurrentSelection()
                        ? theme.palette.primary.contrastText
                        : theme.palette.action.disabled,
                    },
                    '&.Mui-disabled': {
                      backgroundColor: theme.palette.action.disabledBackground,
                      color: theme.palette.action.disabled,
                    }
                  }}
                >
                  <AddIcon />
                </IconButton>
              </Box>
            </Box>
          )}

          {/* Verse Picker */}
          <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden', gap: 1, borderRadius: '10px', border: `1px solid ${theme.palette.outline.light}` }}>
            {/* Books Column */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', borderRight: `1px solid ${theme.palette.outline.light}` }}>
              <Typography variant="subtitle2" sx={{ p: 2, pb: 1, fontWeight: 600, textAlign: 'center', bgcolor: theme.palette.background.paper }}>
                Book
              </Typography>
              <Box sx={{ flex: 1, overflow: 'auto' }}>
                {bibleBooks.map((book) => (
                  <ListItem key={book.id} disablePadding>
                    <ListItemButton
                      selected={book.id === pickerBook}
                      onClick={() => handleBookSelect(book.id)}
                      sx={{
                        py: 1,
                        px: 2,
                        '&.Mui-selected': {
                          backgroundColor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                          '&:hover': {
                            backgroundColor: theme.palette.primary.dark,
                          },
                        },
                      }}
                    >
                      <ListItemText primary={book.name} sx={{ textAlign: 'center', m: 0 }} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </Box>
            </Box>

            {/* Chapters Column */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', borderRight: `1px solid ${theme.palette.outline.light}` }}>
              <Typography variant="subtitle2" sx={{ p: 2, pb: 1, fontWeight: 600, textAlign: 'center', bgcolor: theme.palette.background.paper }}>
                Chapter
              </Typography>
              <Box sx={{ flex: 1, overflow: 'auto' }}>
                {availableChapters.map((chapter) => (
                  <ListItem key={chapter.id} disablePadding>
                    <ListItemButton
                      selected={chapter.number === pickerChapter}
                      onClick={() => handleChapterSelect(chapter.number)}
                      sx={{
                        py: 1,
                        px: 2,
                        '&.Mui-selected': {
                          backgroundColor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                          '&:hover': {
                            backgroundColor: theme.palette.primary.dark,
                          },
                        },
                      }}
                    >
                      <ListItemText primary={chapter.number} sx={{ textAlign: 'center', m: 0 }} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </Box>
            </Box>

            {/* Verses Column */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="subtitle2" sx={{ p: 2, pb: 1, fontWeight: 600, textAlign: 'center', bgcolor: theme.palette.background.paper }}>
                Verse
              </Typography>
              <Box sx={{ flex: 1, overflow: 'auto' }}>
                {availableVerses.map((verse) => (
                  <ListItem key={verse.id} disablePadding>
                    <ListItemButton
                      selected={verse.number === pickerVerse}
                      onClick={() => handleVerseSelect(verse.number)}
                      sx={{
                        py: 1,
                        px: 2,
                        backgroundColor: selectionMode === 'range' && startRef && endRef && verse.number >= startRef.verse && verse.number <= endRef.verse && endRef.chapter === pickerChapter && endRef.book === pickerBook && startRef.chapter === pickerChapter && startRef.book === pickerBook
                          ? `${theme.palette.secondary.main}30`
                          : verse.number === pickerVerse
                            ? theme.palette.primary.main
                            : 'transparent',
                        color: verse.number === pickerVerse
                          ? theme.palette.primary.contrastText
                          : theme.palette.text.primary,
                        '&:hover': {
                          backgroundColor: verse.number === pickerVerse
                            ? theme.palette.primary.dark
                            : selectionMode === 'range' && startRef && endRef && verse.number >= startRef.verse && verse.number <= endRef.verse && endRef.chapter === pickerChapter && endRef.book === pickerBook && startRef.chapter === pickerChapter && startRef.book === pickerBook
                              ? `${theme.palette.secondary.main}50`
                              : `${theme.palette.primary.main}10`,
                        },
                      }}
                    >
                      <ListItemText primary={verse.number} sx={{ textAlign: 'center', m: 0 }} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            p: 3,
            pt: 2,
            borderTop: `1px solid ${theme.palette.outline.light}`,
            gap: 1
          }}
        >
          <Button
            onClick={handleClose}
            sx={{
              px: 3,
              py: 1,
              borderRadius: theme.shape.borderRadius,
              textTransform: 'none',
              fontWeight: 600,
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddPlan}
            variant="contained"
            disabled={!newPlanName.trim() || selectedVersesForPlan.length === 0}
            sx={{
              px: 4,
              py: 1,
              borderRadius: theme.shape.borderRadius * 1.5,
              textTransform: 'none',
              fontWeight: 600,
              background: `linear-gradient(145deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              boxShadow: theme.shadows[2],
              '&:hover': {
                background: `linear-gradient(145deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                boxShadow: theme.shadows[4],
                transform: 'translateY(-1px)'
              },
              '&:disabled': {
                backgroundColor: theme.palette.action.disabled,
                boxShadow: 'none',
                transform: 'none'
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            Create Plan
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Plans;
